import { inject, Injectable } from '@angular/core';
import { getValue } from '@workflow/ecp-workflow/dynamic-app/service/utilities.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { OlaCustomFunctionsService } from '../logic-functions/custom-functions.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { StatefulService } from '@app/service/stateful.service';

const NAME_SPECIFIC_CHARACTERS_ONLY_RX = /^[a-zA-Z][a-zA-z '-]*$/;
const ALPHA_NUMERIC_ONLY_RX = /^[A-Za-z0-9]+(\s[A-Za-z0-9]+)*$/;
const NUMERIC_ONLY_RX = /^[0-9]*$/;
const NO_SPACE_RX = /^[\S]*$/;
const ALPHA_NUMERIC_WITH_LIMITED_SPECIAL_CHARECTERS_ONLY_RX = /^[A-Za-z0-9\s-]*$/;
const ALPHA_SPACE_HYPHEN_ONLY_RX = /^[a-zA-Z\s-]+$/;

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OlaFieldValidationFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly olaCustomFunctionsService: OlaCustomFunctionsService
  ) {}

  /**
   * Validation against given strings.
   * This includes spaces, dashes, apostrophes.
   * @param context field context
   */
  public alphabetsOnly = (context: LogicFnContext): boolean => {
    const value = getValue(context);
    if (typeof value === 'string') {
      return !value || !!value.match(NAME_SPECIFIC_CHARACTERS_ONLY_RX);
    }
  };

  /**
   * Validation against given strings.
   * This includes spaces, dashes.
   * @param context field context
   */
  public alphanumericwithspecialcharacters = (context: LogicFnContext): boolean => {
    // const prefixSpaceHyphenRegx = /^[\s -]*/;
    // const suffixSpaceHyphenRegx = /[A-Za-z0-9\s -]*([\s -])$/;
    const value = getValue(context);
    if (typeof value === 'string') {
      if (!value) {
        return !value;
      }
      const alphanumericIsmatched = !!value.match(ALPHA_NUMERIC_WITH_LIMITED_SPECIAL_CHARECTERS_ONLY_RX);
      const strArray = value.split('');

      const result =
        strArray.length === 0 ||
        (alphanumericIsmatched &&
          strArray[0] !== ' ' &&
          strArray[0] !== '-' &&
          strArray[strArray.length - 1] !== ' ' &&
          strArray[strArray.length - 1] !== '-');

      return result;
    }
  };

  /**
   * Validation against given strings.
   * This includes alphabets, numbers 0-9.
   * @param context field context
   */
  public alphanumeric = (context: LogicFnContext): boolean => {
    const value = getValue(context); // context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(ALPHA_NUMERIC_ONLY_RX);
    }
  };

  /**
   * Validation against given strings.
   * This includes alphabets, space and dash.
   * @param context field context
   */
  public alphaspacehyphenonly = (context: LogicFnContext): boolean => {
    const value = getValue(context);
    if (typeof value === 'string') {
      return !value || !!value.match(ALPHA_SPACE_HYPHEN_ONLY_RX);
    }
  };

  /**
   * Validation against given strings.
   * This includes string without space.
   * @param context field context
   */
  public nospace = (context: LogicFnContext): boolean => {
    const value = getValue(context); // context.field.readableValue;
    if (typeof value === 'string') {
      return !value || !!value.match(NO_SPACE_RX);
    }
  };

  /**
   * Validation against given strings.
   * This includes no empty space.
   * @param context field context
   */
  public noEmptySpace = (context: LogicFnContext): boolean => {
    const value = context.field.readableValue;
    if (typeof value === 'string' && value) {
      const trimedValue = value.trim();
      if (trimedValue.length === 0) {
        return false;
      }
    }
  };

  /**
   * validation against the number
   * @param context field context
   */
  public integerOnly = (context: LogicFnContext): boolean => {
    const value = getValue(context);
    if (typeof value === 'string') {
      return !value || !!value.match(NUMERIC_ONLY_RX);
    }
  };

  /**
   * Validation against given two dates.
   * This includes date format MM/yyyy.
   * @param context field context
   */
  public enddate = (context: LogicFnContext, targetDate: string): boolean => {
    const value = context.field.readableValue;
    if (value && targetDate) {
      const endDateArray = value.split('/');
      const targetDateArray = targetDate.split('/');

      if (!endDateArray || endDateArray.length < 2 || !targetDateArray || targetDateArray.length < 2) {
        return;
      }

      const startDate = new Date(`${targetDateArray[0]}/1/${targetDateArray[1]}`);
      const endDate = new Date(`${endDateArray[0]}/1/${endDateArray[1]}`);

      return endDate > startDate;
    }
  };

  /**
   * Enable continue button when below function valid
   * @param context
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isValidLoanType = (context: LogicFnContext): boolean => {
    let loanGroup = this.statefulService.wrap('ss:ola-new-loan-group').valueString;
    let loanType = this.statefulService.wrap('ss:ola-new-loan-type').value;
    let loanGroups = [];
    if (
      this.adminPortalFeaturesService?.config?.olaConfiguration &&
      this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups
    ) {
      loanGroups = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups;
    }
    let selectedLoanGroups = loanGroups.filter((t) => loanGroup && t.loanGroup === loanGroup);
    let isValid = false;

    if (loanGroup) {
      loanGroup = loanGroup ? loanGroup.toLowerCase() : '';
      isValid =
        (selectedLoanGroups && selectedLoanGroups.length > 0 && !selectedLoanGroups[0].isLoanGroupHasProduct) || loanType ? true : false;

      if (loanGroup || loanType) {
        return isValid;
      }
    }

    loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value;
    selectedLoanGroups = loanGroups.filter((t) => loanGroup && t.loanGroup === loanGroup);

    loanGroup = loanGroup ? loanGroup.toLowerCase() : '';
    isValid =
      (selectedLoanGroups && selectedLoanGroups.length > 0 && !selectedLoanGroups[0].isLoanGroupHasProduct) || loanType ? true : false;
    return isValid;
  };

  public isRequiredLoanDetailDropdown = (context: LogicFnContext, propertyName: string): boolean => {
    const loanProduct = this.getLoanProduct();
    let isRequired = false;
    if (!loanProduct || !loanProduct.olaLoanDetail || !loanProduct.olaLoanDetail[propertyName]) {
      return false;
    }
    const config = loanProduct.olaLoanDetail[propertyName];
    isRequired = config.isRequired as boolean;
    if (!context.field.data.value && config.defaultValue) {
      if (context?.field?.config?.payload?.takeActualId) {
        const picklistValues = this.olaCustomFunctionsService
          .PickListEnumBuilder(
            context,
            'pickListType',
            'takeActualId',
            'excludedOptions',
            'includedOption',
            'witnessType',
            'loanPurposeSubkey'
          )
          .filter((item) => item.label === config.defaultValue);
        if (picklistValues && picklistValues.length > 0) {
          context.field.data.value = picklistValues[0]?.value;
          context.field.config.defaultValue = picklistValues[0]?.value;
        }
      } else {
        context.field.data.value = config.defaultValue;
        context.field.config.defaultValue = config.defaultValue;
      }
    }

    if (isRequired) {
      if (!context.field.hasValidation('required')) {
        context.field.config.validation.push(['required']);
      }
    } else {
      if (context.field.hasValidation('required')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const validations: any = context.field.config.validation;
        context.field.config.validation = validations.filter((t) => t !== 'required' && Array.isArray(t) && t[0] !== 'required');
      }
    }
    return isRequired;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getLoanGroup(): any {
    const selectedLoanGroupName = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    if (
      selectedLoanGroupName &&
      this.adminPortalFeaturesService?.config?.olaConfiguration &&
      this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups
    ) {
      const loanGroup = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups.filter(
        // eslint-disable-next-line eqeqeq
        (t) => t.loanGroup.toLowerCase() == selectedLoanGroupName.toLowerCase()
      );
      return loanGroup && loanGroup.length > 0 ? loanGroup[0] : [];
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getLoanProduct(): any {
    const selectedLoanTypeName = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const loanGroup = this.getLoanGroup();
    if (loanGroup && loanGroup.loanProducts) {
      const loanProduct = loanGroup.loanProducts.filter(
        (t) =>
          // eslint-disable-next-line eqeqeq
          !loanGroup.isLoanGroupHasProduct || (selectedLoanTypeName && t.productName.toLowerCase() == selectedLoanTypeName.toLowerCase())
      );
      return loanProduct && loanProduct.length > 0 ? loanProduct[0] : [];
    }
    return undefined;
  }
}
